import React, { useMemo } from 'react';
import './StateGuidesList.css';

// Import all city data
import soccerData from '../data/cities/soccerCities.json';
import basketballData from '../data/cities/basketballCities.json';
import baseballData from '../data/cities/baseballCities.json';
import balletData from '../data/cities/balletCities.json';
import gymnasticsData from '../data/cities/gymnasticsCities.json';
import martialArtsData from '../data/cities/martialArtsCities.json';
import volleyballData from '../data/cities/volleyballCities.json';
import tennisData from '../data/cities/tennisCities.json';
import runningData from '../data/cities/runningCities.json';
import yogaData from '../data/cities/yogaCities.json';
import danceData from '../data/cities/danceCities.json';
import golfData from '../data/cities/golfCities.json';

const StateGuidesList = ({ className = '' }) => {
  const stateGuides = useMemo(() => {
    // Combine all sports data
    const allCities = [
      ...soccerData.map((city) => ({ ...city, sport: 'soccer' })),
      ...basketballData.map((city) => ({ ...city, sport: 'basketball' })),
      ...baseballData.map((city) => ({ ...city, sport: 'baseball' })),
      ...balletData.map((city) => ({ ...city, sport: 'ballet' })),
      ...gymnasticsData.map((city) => ({ ...city, sport: 'gymnastics' })),
      ...martialArtsData.map((city) => ({ ...city, sport: 'martial-arts' })),
      ...volleyballData.map((city) => ({ ...city, sport: 'volleyball' })),
      ...tennisData.map((city) => ({ ...city, sport: 'tennis' })),
      ...runningData.map((city) => ({ ...city, sport: 'running' })),
      ...yogaData.map((city) => ({ ...city, sport: 'yoga' })),
      ...danceData.map((city) => ({ ...city, sport: 'dance' })),
      ...golfData.map((city) => ({ ...city, sport: 'golf' })),
    ];

    // Remove duplicates based on sport, state, and slug combination
    const uniqueCities = allCities.filter(
      (city, index, self) =>
        index ===
        self.findIndex(
          (c) =>
            c.sport === city.sport &&
            c.state === city.state &&
            c.slug === city.slug
        )
    );

    // Group by state
    const groupedByState = uniqueCities.reduce((acc, city) => {
      const state = city.state;
      if (!acc[state]) {
        acc[state] = [];
      }
      acc[state].push(city);
      return acc;
    }, {});

    // Sort states alphabetically and cities within each state
    return Object.keys(groupedByState)
      .sort()
      .reduce((acc, state) => {
        acc[state] = groupedByState[state].sort((a, b) =>
          a.city.localeCompare(b.city)
        );
        return acc;
      }, {});
  }, []);

  const getSportDisplayName = (sport) => {
    const sportNames = {
      soccer: 'Youth Soccer',
      basketball: 'Youth Basketball',
      baseball: 'Youth Baseball',
      ballet: 'Youth Ballet',
      gymnastics: 'Youth Gymnastics',
      'martial-arts': 'Youth Martial Arts',
      volleyball: 'Youth Volleyball',
      tennis: 'Youth Tennis',
      running: 'Youth Running',
      yoga: 'Youth Yoga',
      dance: 'Youth Dance',
      golf: 'Youth Golf',
    };
    return sportNames[sport] || sport;
  };

  const getStateDisplayName = (stateCode) => {
    const stateNames = {
      CA: 'California',
      FL: 'Florida',
      TX: 'Texas',
      NY: 'New York',
      IL: 'Illinois',
      PA: 'Pennsylvania',
      OH: 'Ohio',
      GA: 'Georgia',
      NC: 'North Carolina',
      MI: 'Michigan',
      NJ: 'New Jersey',
      VA: 'Virginia',
      WA: 'Washington',
      AZ: 'Arizona',
      MA: 'Massachusetts',
      TN: 'Tennessee',
      IN: 'Indiana',
      MO: 'Missouri',
      MD: 'Maryland',
      CO: 'Colorado',
      MN: 'Minnesota',
      WI: 'Wisconsin',
      AL: 'Alabama',
      SC: 'South Carolina',
      LA: 'Louisiana',
      KY: 'Kentucky',
      OR: 'Oregon',
      OK: 'Oklahoma',
      CT: 'Connecticut',
      UT: 'Utah',
      IA: 'Iowa',
      NV: 'Nevada',
      AR: 'Arkansas',
      MS: 'Mississippi',
      KS: 'Kansas',
      NE: 'Nebraska',
      ID: 'Idaho',
      NH: 'New Hampshire',
      ME: 'Maine',
      NM: 'New Mexico',
      HI: 'Hawaii',
      RI: 'Rhode Island',
      MT: 'Montana',
      DE: 'Delaware',
      SD: 'South Dakota',
      ND: 'North Dakota',
      AK: 'Alaska',
      DC: 'District of Columbia',
      VT: 'Vermont',
      WY: 'Wyoming',
      WV: 'West Virginia',
    };
    return stateNames[stateCode] || stateCode;
  };

  return (
    <section className={`state-guides-section ${className}`}>
      <div className='container'>
        <h2>Youth Sports Guides by State</h2>
        <p className='section-description'>
          Find comprehensive youth sports guides for your area. Each guide
          includes local leagues, clubs, fields, camps, and resources to help
          your child succeed in their chosen sport.
        </p>

        <div className='states-grid'>
          {Object.entries(stateGuides).map(([stateCode, cities]) => (
            <div key={stateCode} className='state-section'>
              <h3 className='state-name'>{getStateDisplayName(stateCode)}</h3>
              <div className='cities-list'>
                {cities.map((city) => (
                  <a
                    key={`${city.sport}-${city.state}-${city.slug}`}
                    href={`/${city.slug}`}
                    className='city-link'
                  >
                    {getSportDisplayName(city.sport)} Guide in {city.city}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StateGuidesList;
